import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import store from '../../store';
import MoreOptions from '@/app/shared/models/trip-segments.model';
import { Inject } from 'inversify-props';
import { SavedTripsService } from '@/app/services/saved-trips.service';
import { TimeService } from '@/app/services/time.service';
import SkeletonAnimation from '@/app/shared/components/skeleton-animation/skeleton-animation.vue';
import Helpers from '@/app/shared/common/helpers';
import {AffiliateConfigurationService} from "@/app/services/affiliate-configuration.service";
import {FULL_BASE_URL} from '@/app/shared/common/constants'

const moment = require('moment');

@Component<SavedTripsComponent>({
  components: {
    SkeletonAnimation
  }
})

export default class SavedTripsComponent extends Vue {
  @Inject() private readonly savedTripsService: SavedTripsService;
  @Inject() private readonly timeService: TimeService;
  @Inject() private readonly affiliateConfigurationService: AffiliateConfigurationService;
  public Helpers: Helpers = Helpers;
  public moreOptions: Array<MoreOptions> = [];
  public response: any;
  public itinerary: Array<any> = [];
  public trips: Array<any> = [];
  public loadedTrips: Array<any> = [];
  public tripsCurrencySigns: Array<string> = [];
  public favoritesId: Array<number> = [];
  public currencyCoefficient: number = 0;
  public pagesOfMoreOptions: Array<Array<MoreOptions>> = [];
  public isSearching = true;
  public favoritesIsEmpty = false;
  public moment = moment;
  public tripsLoadingKey = Math.random();

  private currencyResponse: any;
  private toasts = {
    favDeleted: {duration: 5000, message: "The saved trip was deleted"},
    favDeleteError: {duration: 5000, message: "Could not remove the saved trip"}
  };

  private savedAffiliateUrl = '';
  private savedAffiliateUrlKey = 1;

  constructor() {
    super();
    if (store.state.more_options.length > 0) {
      this.moreOptions = store.state.more_options;
    }
    this.$nextTick(() => {
      this.getFavorites();
      this.getCurrency();
    })
  }

  private updateLoadedTrips(){
    let loaded = [];
    for(let i in this.trips){
      if(this.trips[i] && this.trips[i].hasOwnProperty('segments_normalized')){
        loaded.push(this.trips[i]);
      }
    }
    this.loadedTrips = loaded;
    return this.loadedTrips;
  }

  public removeTrip(index: number) {
    this.savedTripsService.removeTrip({
      favorite_id: this.favoritesId[index]
    }).then(res => {
      Helpers.removeArrItem(this.trips, index);
      this.$toasted.success(this.toasts.favDeleted.message, {duration: this.toasts.favDeleted.duration});
    }).catch(err => {
      this.$toasted.info(this.toasts.favDeleteError.message, {duration: this.toasts.favDeleteError.duration});
    })
  }

  public async editTrip(id: number, showChangePanel=false) {
    this.$store.dispatch("autoSelectFirstOptionForAllLegs", {});
    if(showChangePanel){
      this.$store.dispatch("activateChangeFlightsPanel", {});
    }
    this.$router.push(this.getTripUrl(id));
  }

  private getTripUrl(id: number, fullUrl = false): string{
    let url = fullUrl ? FULL_BASE_URL + '/' : '';
    return url + `search-result/${this.trips[id].itinerary_id}/${this.trips[id].solutions_info.solutions[0].result_id}`;
  }

  private getCurrency() {
    this.savedTripsService.getCurrency()
      .then((res: any) => {
        this.currencyResponse = res.data;
      })
  }

  private getFavorites() {
    this.savedTripsService.getFavorites({})
      .then(res => {
        if (res.data.response.favorites.length < 1) {
          this.favoritesIsEmpty = true;
          this.isSearching = false;
          return;
        }
        this.trips = new Array<any>(res.data.response.favorites.length);
        for (let i = 0; i < res.data.response.favorites.length; i++) {
          this.getTrip(res.data.response.favorites[i].itinerary_id, res.data.response.favorites[i].result_id, i);
          this.favoritesId.push(res.data.response.favorites[i].id);
        }
      })
  }

  private getTrip(itineraryId: number, resultId: number, forFavIdx: number|undefined = undefined){
    this.isSearching = true;
    this.savedTripsService.getFlightsResults({
      itinerary_id: itineraryId,
      filter: 1,
      result_id: resultId
    }).then(res => {
      this.isSearching = false;
      this.pagesOfMoreOptions.push([]);
      let trip = res.data.response;
      // Do some date/time formatting
      trip = this.formatAllTripDateTimes(trip);
      if(forFavIdx !== undefined && !isNaN(forFavIdx)){
        this.trips[forFavIdx] = trip;
        this.tripsCurrencySigns[forFavIdx] = this.getCurrencySign(res.data.response.currency);
      } else {
        this.trips.push(trip);
        this.tripsCurrencySigns.push(this.getCurrencySign(res.data.response.currency));
      }
      this.$nextTick(() => {
        this.updateLoadedTrips();
        this.tripsLoadingKey = this.tripsLoadingKey + Math.random();
      });

    }).catch(err => {
      this.isSearching = false;
    })
  }

  /**
   * Formats date and times in a trip object.
   *
   * TODO: Maybe move this to Helpers/Utils
   *
   * @param trip
   */
  private formatAllTripDateTimes(trip: any){
    for(let fIdx in trip.fares){
      let fareDepartureDateStr = trip.fares[fIdx].departure.date;
      let fareDepartureTimeStr = trip.fares[fIdx].departure.time;
      let fareArrivalDateStr = trip.fares[fIdx].arrival.date;
      let fareArrivalTimeStr = trip.fares[fIdx].arrival.time;
      trip.fares[fIdx].departure.date_fmt = moment(fareDepartureDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
      trip.fares[fIdx].departure.time_fmt = moment(fareDepartureTimeStr, "HHmm").format("HH:mm");
      trip.fares[fIdx].arrival.date_fmt = moment(fareArrivalDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
      trip.fares[fIdx].arrival.time_fmt = moment(fareArrivalTimeStr, "HHmm").format("HH:mm");

      for (let sIdx in trip.fares[fIdx].segments){
        let segDepartureDateStr = trip.fares[fIdx].segments[sIdx].departure.date;
        let segDepartureTimeStr = trip.fares[fIdx].segments[sIdx].departure.time;
        let segArrivalDateStr = trip.fares[fIdx].segments[sIdx].arrival.date;
        let segArrivalTimeStr = trip.fares[fIdx].segments[sIdx].arrival.time;
        trip.fares[fIdx].segments[sIdx].departure.date_fmt = moment(segDepartureDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
        trip.fares[fIdx].segments[sIdx].departure.time_fmt = moment(segDepartureTimeStr, "HHmm").format("HH:mm");
        trip.fares[fIdx].segments[sIdx].arrival.date_fmt = moment(segArrivalDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
        trip.fares[fIdx].segments[sIdx].arrival.time_fmt = moment(segArrivalTimeStr, "HHmm").format("HH:mm");

        for(let stIdx in trip.fares[fIdx].segments[sIdx].stops){
          let stopDepartureDateStr = trip.fares[fIdx].segments[sIdx].stops[stIdx].departure.date;
          let stopDepartureTimeStr = trip.fares[fIdx].segments[sIdx].stops[stIdx].departure.time;
          let stopArrivalDateStr = trip.fares[fIdx].segments[sIdx].stops[stIdx].departure.date;
          let stopArrivalTimeStr = trip.fares[fIdx].segments[sIdx].stops[stIdx].departure.date;
          trip.fares[fIdx].segments[sIdx].stops[stIdx].departure.date_fmt = moment(stopDepartureDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
          trip.fares[fIdx].segments[sIdx].stops[stIdx].departure.time_fmt = moment(stopDepartureTimeStr, "HHmm").format("HH:mm");
          trip.fares[fIdx].segments[sIdx].stops[stIdx].arrival.date_fmt = moment(stopArrivalDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
          trip.fares[fIdx].segments[sIdx].stops[stIdx].arrival.time_fmt = moment(stopArrivalTimeStr, "HHmm").format("HH:mm");
        }
      }
    }
    if(trip.segments_normalized){
      for(let sIdx in trip.segments_normalized){
        let segDepartureDateStr = trip.segments_normalized[sIdx].departure.date;
        let segDepartureTimeStr = trip.segments_normalized[sIdx].departure.time;
        let segArrivalDateStr = trip.segments_normalized[sIdx].arrival.date;
        let segArrivalTimeStr = trip.segments_normalized[sIdx].arrival.time;
        trip.segments_normalized[sIdx].departure.date_fmt = moment(segDepartureDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
        trip.segments_normalized[sIdx].departure.time_fmt = moment(segDepartureTimeStr, "HHmm").format("HH:mm");
        trip.segments_normalized[sIdx].arrival.date_fmt = moment(segArrivalDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
        trip.segments_normalized[sIdx].arrival.time_fmt = moment(segArrivalTimeStr, "HHmm").format("HH:mm");
        for(let stIdx in trip.segments_normalized[sIdx].stops){
          let stopDepartureDateStr = trip.segments_normalized[sIdx].stops[stIdx].departure.date;
          let stopDepartureTimeStr = trip.segments_normalized[sIdx].stops[stIdx].departure.time;
          let stopArrivalDateStr = trip.segments_normalized[sIdx].stops[stIdx].departure.date;
          let stopArrivalTimeStr = trip.segments_normalized[sIdx].stops[stIdx].departure.date;
          trip.segments_normalized[sIdx].stops[stIdx].departure.date_fmt = moment(stopDepartureDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
          trip.segments_normalized[sIdx].stops[stIdx].departure.time_fmt = moment(stopDepartureTimeStr, "HHmm").format("HH:mm");
          trip.segments_normalized[sIdx].stops[stIdx].arrival.date_fmt = moment(stopArrivalDateStr, "DDMMYY").format("ddd DD-MMM-YYYY");
          trip.segments_normalized[sIdx].stops[stIdx].arrival.time_fmt = moment(stopArrivalTimeStr, "HHmm").format("HH:mm");
        }
      }
    }
    return trip;
  }

  /**
   * Returns a list of lists, each second-level list containing non-overland parts of a trip.
   * For example a trip usually known as AMS-LON-VLC-000-LIS-NYC will be returned as:
   *    [["AMS", "LON", "VLC"],["LIS", "NYC"]]
   *
   * The list contains Parent City Codes
   * To build the list, this goes into fares -> segments but not into each segment's stops
   *
   * @param trip
   */
  public getTripRouteLists(trip: any){
    let routeLists: Array<string>[] = [];
    let nonOverlandList: string[] = [];
    for (let snIdx in trip.segments_normalized){
      let sDeparture = trip.segments_normalized[snIdx].departure.parent_city_code;
      let sArrival = trip.segments_normalized[snIdx].arrival.parent_city_code;
      if(nonOverlandList.length === 0){
        nonOverlandList.push(sDeparture);
        nonOverlandList.push(sArrival);
        continue;
      }
      let prevArrival = nonOverlandList[nonOverlandList.length - 1];
      if(sDeparture == prevArrival){
        nonOverlandList.push(sArrival);
      } else {
        routeLists.push(nonOverlandList);
        nonOverlandList = [];
        nonOverlandList.push(sDeparture);
        nonOverlandList.push(sArrival);
      }
    }
    routeLists.push(nonOverlandList);
     return routeLists;
  }

  public joinRoute(routeAsList: string[]){
    return routeAsList.join("-");
  }

  /**
   * @deprecated
   *
   * This is deprecated because it does not correctly get the favorite trips.
   * Instead it gets all the results(packages) in the solution to which the saved trip belongs.
   * @see getTrip
   *
   * @param itinerary_id
   */
  private getTripsOld(itinerary_id: number) {
    this.savedTripsService.getSolutions({
      itinerary_id: itinerary_id
    })
      .then(res => {
        let response = res.data.response.solutions_info;
        this.savedTripsService.getFlightsResults({
          itinerary_id: itinerary_id,
          filter: 1,
          results_id: response.solutions
        })
          .then(res => {
            this.isSearching = false;
            this.pagesOfMoreOptions.push([]);
            this.trips.push(res.data.response);


            for (let i = 0; i < this.trips[this.trips.length - 1].fares.length; i++) {
              this.trips[this.trips.length - 1].fares[i].departure.date = moment(this.trips[this.trips.length - 1].fares[i].departure.date, 'DDMMYY').format('DD-MMM-YYYY')
              for (let j = 0; j < this.trips[this.trips.length - 1].fares[i].segments.length; j++) {
                // Reformat Departure/Arrival Date/Time and time for: last trip -> each fare -> each segment -> first stop
                let departureDate = moment(this.trips[this.trips.length - 1].fares[i].segments[j].stops[0].departure.date, 'DDMMYY').format("ddd DD-MMM-YYYY");
                let departureTime = moment(this.trips[this.trips.length - 1].fares[i].segments[j].stops[0].departure.time, 'HHmm').format('HH:mm');
                this.trips[this.trips.length - 1].fares[i].segments[j].stops[0].departure.date = departureDate;
                this.trips[this.trips.length - 1].fares[i].segments[j].stops[0].departure.time = departureTime;

                let arrivalDate = moment(this.trips[this.trips.length - 1].fares[i].segments[j].stops[this.trips[this.trips.length - 1].fares[i].segments[j].stops.length - 1].arrival.date, 'DDMMYY').format("ddd DD-MMM-YYYY");
                let arrivalTime = moment(this.trips[this.trips.length - 1].fares[i].segments[j].stops[this.trips[this.trips.length - 1].fares[i].segments[j].stops.length - 1].arrival.time, 'HHmm').format('HH:mm');
                this.trips[this.trips.length - 1].fares[i].segments[j].stops[this.trips[this.trips.length - 1].fares[i].segments[j].stops.length - 1].arrival.date = arrivalDate;
                this.trips[this.trips.length - 1].fares[i].segments[j].stops[this.trips[this.trips.length - 1].fares[i].segments[j].stops.length - 1].arrival.time = arrivalTime;

                this.trips[this.trips.length - 1].fares[i].segments[j].duration = this.timeService.flightTimeFormat(this.trips[this.trips.length - 1].fares[i].segments[j].duration);
                this.pagesOfMoreOptions[this.pagesOfMoreOptions.length - 1].push(this.trips[this.trips.length - 1].fares[i].segments[j]);
              }
            }

            this.trips[this.trips.length - 1].price = this.getCurrencyCoficient(res.data.response.currency, res.data.response.price);
            this.tripsCurrencySigns.push(this.getCurrencySign(res.data.response.currency));
          })
      })
  }


  private getCurrencyCoficient(currency: string, price: number) {
    const currencyValue = Object.keys(this.currencyResponse).map(i => this.currencyResponse[i])
    const currencyKey = Object.keys(this.currencyResponse);

    for (let i = 0; i < currencyKey.length; i++) {
      if (currencyKey[i] === 'USD' + currency) {
        this.currencyCoefficient = currencyValue[i];
        return (price * currencyValue[i]).toFixed();
      }
    }

    return price;

  }

  private getCurrencySign(currency: string) {
    switch (currency) {
      case 'EUR':
        return '€';
      case 'INR':
        return '₹';
      case 'MYR':
        return 'RM';
      case 'THB':
        return '฿';
      case 'JPY':
        return '¥';
      case 'GBP':
        return '£';
      default:
        return '$';
    }
  }

  public createAffiliateUrl(tripId: number) {
    if(this.savedAffiliateUrl.length){
      this.savedAffiliateUrl = '';
    }
    let url = this.getTripUrl(tripId, true);
    let name = this.getTripRouteLists(this.trips[tripId]).map(list => this.joinRoute(list)).join(', ');
    this.affiliateConfigurationService.addUrl(
        {
          'url-data': {name, url, id: 0}
        }
    ).then((res: any) => {
      this.savedAffiliateUrl = AffiliateConfigurationService.makeAffiliateUrl(url, res.data.user_id, res.data.url_id);
    });
    return false;
  }

  public onAffiliateUrlDismiss(){
    this.savedAffiliateUrlKey++;
    this.savedAffiliateUrl = '';
  }

  public copyToClipboard(text: string) {
    //@ts-ignore
    this.$clipboard(text);
    //@ts-ignore
    this.$toasted.info("The URL was copied!", {duration: 1000});
  }


};
